import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { API } from "../lib/api";
import { useSiteContent } from "../lib/siteContent";
import { SiteHeader } from "../components/SiteHeader";
import { SomaticTexture } from "../components/SomaticTexture";
import { ArrowLeft, X, ChevronLeft, ChevronRight, Lock, Loader2, Images } from "lucide-react";

const SERIF = '"Cormorant Garamond", Georgia, serif';
const SANS = '"Plus Jakarta Sans", system-ui, sans-serif';
const PREVIEW_COUNT = 8;
const SPANS = ["col-span-12 sm:col-span-7", "col-span-12 sm:col-span-5", "col-span-12 sm:col-span-5", "col-span-12 sm:col-span-7", "col-span-12 sm:col-span-6", "col-span-12 sm:col-span-6", "col-span-12 sm:col-span-7", "col-span-12 sm:col-span-5"];

export default function GalleryPreview() {
  const site = useSiteContent();
  const [items, setItems] = useState([]);
  const [totalArt, setTotalArt] = useState(0);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    axios.get(`${API}/gallery`).then(({ data }) => { setItems(data.items || []); setTotalArt(data.total_art || 0); }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const plates = useMemo(() => items.filter((i) => i.kind === "art").slice(0, PREVIEW_COUNT), [items]);

  const close = useCallback(() => setLightbox(null), []);
  const step = useCallback((dir) => {
    setLightbox((cur) => (cur == null ? cur : Math.min(plates.length - 1, Math.max(0, cur + dir))));
  }, [plates.length]);

  useEffect(() => {
    if (lightbox == null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, close, step]);

  const plateCount = useMemo(() => items.filter((i) => i.kind === "art").slice(0, PREVIEW_COUNT).length, [items]);
  const nextSlide = useCallback(() => setSlide((s) => (plateCount ? (s + 1) % plateCount : 0)), [plateCount]);
  const prevSlide = useCallback(() => setSlide((s) => (plateCount ? (s - 1 + plateCount) % plateCount : 0)), [plateCount]);

  // Auto-advance the teaser carousel; pause while the lightbox is open.
  useEffect(() => {
    if (lightbox != null || plateCount === 0) return;
    const t = setInterval(nextSlide, 6000);
    return () => clearInterval(t);
  }, [lightbox, plateCount, nextSlide]);

  // Arrow keys drive the carousel when the lightbox is closed.
  useEffect(() => {
    if (lightbox != null) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") nextSlide();
      if (e.key === "ArrowLeft") prevSlide();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, nextSlide, prevSlide]);

  const active = lightbox != null ? plates[lightbox] : null;

  if (loading) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center" style={{ background: "#0C0E12" }}>
        <Loader2 className="h-6 w-6 animate-spin text-[#D4A373]" />
      </div>
    );
  }

  return (
    <div className="relative min-h-[100dvh]" style={{ background: "#0C0E12", fontFamily: SANS }} data-testid="gallery-preview-page">
      <SomaticTexture />

      <SiteHeader theme="dark" right={
        <Link to="/vault/gallery" data-testid="gallery-preview-unlock-top"
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-medium transition-transform hover:scale-[1.02]"
          style={{ background: "#D4A373", color: "#0C0E12" }}>
          <Lock className="h-3.5 w-3.5" /> Unlock the full gallery
        </Link>
      } />

      <section className="relative z-10 mx-auto max-w-6xl px-5 pb-8 pt-16 sm:px-8 sm:pt-24">
        <div className="pointer-events-none absolute left-1/2 top-0 h-72 w-[46rem] max-w-[90vw] -translate-x-1/2"
          style={{ background: "radial-gradient(60% 100% at 50% 0%, rgba(212,163,115,0.16), transparent 70%)" }} />
        <span className="relative inline-flex h-11 w-11 items-center justify-center rounded-full" style={{ background: "rgba(212,163,115,0.12)", color: "#D4A373" }}><Images className="h-5 w-5" /></span>
        <p data-testid="gallery-preview-meta" className="relative mt-5 text-[11px] uppercase tracking-[0.35em]" style={{ color: "#98A898" }}>A first look · {plates.length} of {totalArt} somatic plates</p>
        <h1 data-testid="gallery-preview-title" className="relative mt-4 text-5xl leading-[0.95] sm:text-6xl lg:text-7xl" style={{ fontFamily: SERIF, color: "#F7F5F0", fontWeight: 500 }}>
          A taste of the gallery
        </h1>
        <p className="relative mt-5 max-w-xl text-lg italic" style={{ fontFamily: SERIF, color: "#D4A373" }}>Paintings &amp; words by Casey Muze — open to everyone.</p>
        <p className="relative mt-5 max-w-lg text-sm leading-relaxed" style={{ color: "#A3A8B2" }} data-testid="gallery-preview-intro">
          {site?.gallery?.intro || "A few plates from the full exhibition. Take one breath, then wander. The complete room — every painting and reflection — waits inside the book and the Language Vault."}
        </p>
      </section>

      <section className="relative z-10 mx-auto max-w-5xl px-5 pb-6 sm:px-8" data-testid="gallery-teaser-carousel">
        <div className="relative overflow-hidden rounded-sm" style={{ background: "#12151C", boxShadow: "0 30px 90px -40px rgba(0,0,0,0.95)", border: "1px solid rgba(255,255,255,0.06)" }}>
          <div className="relative aspect-[16/11] w-full sm:aspect-[2/1]">
            <AnimatePresence mode="wait">
              {plates[slide] && (
                <motion.button
                  key={plates[slide].index}
                  type="button"
                  onClick={() => setLightbox(slide)}
                  data-testid={`gallery-teaser-slide-${slide}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                  drag="x"
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.18}
                  onDragEnd={(e, info) => { if (info.offset.x < -60) nextSlide(); else if (info.offset.x > 60) prevSlide(); }}
                  className="absolute inset-0 block h-full w-full cursor-pointer"
                >
                  <img src={plates[slide].image} alt={plates[slide].title} draggable={false}
                    className="h-full w-full object-cover" style={{ filter: "brightness(0.8) saturate(1.02)" }} />
                  <div className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(to top, rgba(12,14,18,0.94) 0%, rgba(12,14,18,0.3) 48%, transparent 78%)" }} />
                  <div className="absolute inset-x-0 bottom-0 p-6 text-left sm:p-10">
                    <p className="text-[10px] uppercase tracking-[0.3em]" style={{ color: "#98A898" }}>Plate {String(slide + 1).padStart(2, "0")} · {slide + 1} / {plates.length}</p>
                    <p className="mt-2 text-3xl leading-tight sm:text-4xl" style={{ fontFamily: SERIF, color: "#F7F5F0" }}>{plates[slide].title}</p>
                    <p className="mt-2.5 max-w-xl text-base italic leading-relaxed sm:text-xl" style={{ fontFamily: SERIF, color: "#D4A373" }}>{plates[slide].caption}</p>
                  </div>
                </motion.button>
              )}
            </AnimatePresence>

            <button type="button" onClick={prevSlide} aria-label="Previous slide" data-testid="gallery-teaser-prev"
              className="absolute left-3 top-1/2 z-10 -translate-y-1/2 rounded-full p-2.5 text-[#E8E2D9] transition hover:text-white sm:left-5" style={{ background: "rgba(0,0,0,0.38)", backdropFilter: "blur(6px)" }}>
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button type="button" onClick={nextSlide} aria-label="Next slide" data-testid="gallery-teaser-next"
              className="absolute right-3 top-1/2 z-10 -translate-y-1/2 rounded-full p-2.5 text-[#E8E2D9] transition hover:text-white sm:right-5" style={{ background: "rgba(0,0,0,0.38)", backdropFilter: "blur(6px)" }}>
              <ChevronRight className="h-6 w-6" />
            </button>

            {slide === 0 && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 0.7 }}
                data-testid="gallery-teaser-swipe-hint"
                className="pointer-events-none absolute right-4 top-4 z-10 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] uppercase tracking-[0.2em]"
                style={{ background: "rgba(0,0,0,0.42)", color: "#E8E2D9", backdropFilter: "blur(6px)" }}
              >
                <motion.span animate={{ x: [0, 4, 0] }} transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}>↔</motion.span>
                Swipe to explore
              </motion.div>
            )}
          </div>

          <div className="flex items-center justify-center gap-2 py-4" data-testid="gallery-teaser-dots">
            {plates.map((p, i) => (
              <button key={p.index} type="button" onClick={() => setSlide(i)} aria-label={`Go to slide ${i + 1}`} data-testid={`gallery-teaser-dot-${i}`}
                className="h-2 rounded-full transition-all duration-500" style={{ width: i === slide ? "22px" : "8px", background: i === slide ? "#D4A373" : "rgba(255,255,255,0.25)" }} />
            ))}
          </div>
        </div>
        <p className="mt-3 text-center text-xs" style={{ color: "#98A898" }}>Tap a slide to view it larger · {plates.length} of {totalArt} plates</p>
      </section>

      {/* Unlock banner */}
      <section className="relative z-10 mx-auto max-w-6xl px-5 pb-24 pt-6 sm:px-8">
        <div className="relative overflow-hidden rounded-sm p-8 text-center sm:p-12" style={{ background: "#12151C", border: "1px solid rgba(212,163,115,0.2)" }} data-testid="gallery-preview-unlock-banner">
          <div className="pointer-events-none absolute inset-0 opacity-60" style={{ background: "radial-gradient(70% 60% at 50% 0%, rgba(212,163,115,0.12), transparent 70%)" }} />
          <span className="relative mx-auto flex h-12 w-12 items-center justify-center rounded-full" style={{ background: "rgba(212,163,115,0.12)", color: "#D4A373" }}><Lock className="h-5 w-5" /></span>
          <h2 className="relative mt-5 text-3xl sm:text-4xl" style={{ fontFamily: SERIF, color: "#F7F5F0" }}>See all {totalArt} plates</h2>
          <p className="relative mx-auto mt-4 max-w-md text-sm leading-relaxed" style={{ color: "#A3A8B2" }}>
            The complete exhibition — every painting, its words, and a high-resolution print of each — lives inside the full Art Gallery, held by the book and the Language Vault.
          </p>
          <Link to="/vault/gallery" data-testid="gallery-preview-unlock"
            className="relative mt-6 inline-flex items-center justify-center gap-2 rounded-full px-7 py-3 text-sm font-medium transition-transform hover:scale-[1.02]"
            style={{ background: "#D4A373", color: "#0C0E12" }}>
            Unlock the full gallery
          </Link>
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8" style={{ background: "rgba(8,9,12,0.96)", backdropFilter: "blur(8px)" }}
            onClick={close} data-testid="gallery-preview-lightbox">
            <button type="button" onClick={close} aria-label="Close" data-testid="gallery-preview-lightbox-close"
              className="absolute right-4 top-4 rounded-full p-2 text-[#A3A8B2] transition-colors hover:text-white" style={{ background: "rgba(255,255,255,0.06)" }}>
              <X className="h-5 w-5" />
            </button>
            {lightbox > 0 && (
              <button type="button" onClick={(e) => { e.stopPropagation(); step(-1); }} aria-label="Previous" data-testid="gallery-preview-lightbox-prev"
                className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full p-2.5 text-[#A3A8B2] transition-colors hover:text-white sm:left-6" style={{ background: "rgba(255,255,255,0.06)" }}>
                <ChevronLeft className="h-6 w-6" />
              </button>
            )}
            {lightbox < plates.length - 1 && (
              <button type="button" onClick={(e) => { e.stopPropagation(); step(1); }} aria-label="Next" data-testid="gallery-preview-lightbox-next"
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-2.5 text-[#A3A8B2] transition-colors hover:text-white sm:right-6" style={{ background: "rgba(255,255,255,0.06)" }}>
                <ChevronRight className="h-6 w-6" />
              </button>
            )}
            <motion.div key={active.index} initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="grid max-h-full w-full max-w-5xl items-center gap-6 lg:grid-cols-[1.4fr_1fr]" onClick={(e) => e.stopPropagation()}>
              <img src={active.image} alt={active.title} className="max-h-[72vh] w-full rounded-sm object-contain" style={{ boxShadow: "0 40px 120px -40px rgba(0,0,0,1)" }} />
              <div className="text-left">
                <p className="text-[10px] uppercase tracking-[0.3em]" style={{ color: "#98A898" }}>Plate {String(lightbox + 1).padStart(2, "0")}</p>
                <p className="mt-3 text-3xl leading-tight" style={{ fontFamily: SERIF, color: "#F7F5F0" }}>{active.title}</p>
                <p className="mt-4 text-base italic leading-relaxed" data-testid="gallery-preview-lightbox-caption" style={{ fontFamily: SERIF, color: "#D4A373" }}>{active.caption}</p>
                <Link to="/vault/gallery" data-testid="gallery-preview-lightbox-unlock"
                  className="mt-6 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-transform hover:scale-[1.02]"
                  style={{ background: "rgba(212,163,115,0.14)", color: "#E0A96D", border: "1px solid rgba(212,163,115,0.3)" }}>
                  <Lock className="h-4 w-4" /> Unlock the full gallery
                </Link>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
